import { useState } from 'react'
import {
  Box,
  Typography,
  Chip,
  TextField,
  Button,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material'
import {
  Delete as DeleteIcon,

} from '@mui/icons-material'
import { INTERVIEW_ROUND_TYPE_LABELS } from '../../constants/interview'
import { toDateTimeLocalInput } from '../../utils/dateFormat'
import { useNotification } from '../../hooks/useNotification'
import DialogShell from '../common/DialogShell'
import type { InterviewRoundOutcome } from '../../services/api'

export interface InterviewRound {
  roundNumber: number
  type: string
  date?: string | null
  format?: string | null
  interviewerName?: string
  interviewerTitle?: string
  notes?: string
  debriefNotes?: string
  debriefCompletedAt?: string | null
  // Follow-through on the round: the thank-you note, the one-week follow-up,
  // and how it ended. Absent on every round created before these existed — no
  // migration was run — so undefined and null mean the same thing here.
  thankYouSentAt?: string | null
  followUpSentAt?: string | null
  outcome?: InterviewRoundOutcome | null
  status: 'scheduled' | 'completed' | 'debriefed'
  createdAt: string
}

interface InterviewRoundCardProps {
  round: InterviewRound
  onUpdateRound: (roundNumber: number, updates: Record<string, unknown>) => Promise<void>
  onDeleteRound: (roundNumber: number) => Promise<void>
  readOnly?: boolean
}

const formatLabels: Record<string, string> = INTERVIEW_ROUND_TYPE_LABELS

const statusColors: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  scheduled: 'info',
  completed: 'warning',
  debriefed: 'success',
}

// Key order is the dropdown's order — the Select maps over Object.keys.
const OUTCOME_LABELS: Record<InterviewRoundOutcome, string> = {
  offer: 'Offer',
  counter_offer: 'Counter-offer',
  silence: 'Silence',
  rejection: 'Rejection',
  proceed_to_next_stage: 'Proceeding to the next round',
}

// 'success' is reserved for a settled offer. 'info' is live but unsettled —
// the client is moving to another round, or terms are being negotiated — which
// is good news, but not the good news 'offer' means.
const OUTCOME_COLORS: Record<
  InterviewRoundOutcome,
  'success' | 'warning' | 'error' | 'info'
> = {
  offer: 'success',
  counter_offer: 'info',
  silence: 'warning',
  rejection: 'error',
  proceed_to_next_stage: 'info',
}

const DAY_MS = 24 * 60 * 60 * 1000
// MissFit's cadence, from their own walkthrough: the thank-you note goes out
// the day after the round, the follow-up a week later. These only drive the
// on-card "overdue" wording — nothing here schedules an email or feeds the
// daily digest.
const THANK_YOU_DUE_AFTER_DAYS = 1
const FOLLOW_UP_DUE_AFTER_DAYS = 7

const formatSentOn = (iso: string) => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function InterviewRoundCard({ round, onUpdateRound, onDeleteRound, readOnly }: InterviewRoundCardProps) {
  const [debriefOpen, setDebriefOpen] = useState(false)
  const [debriefNotes, setDebriefNotes] = useState(round.debriefNotes || '')
  const [saving, setSaving] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [savingFollowThrough, setSavingFollowThrough] = useState(false)
  const [dateInput, setDateInput] = useState(toDateTimeLocalInput(round.date))
  const [savingDate, setSavingDate] = useState(false)
  const { showError } = useNotification()

  const isPastDate = round.date && new Date(round.date) < new Date()
  const needsDebrief = isPastDate && !round.debriefCompletedAt

  // Days since the round actually happened, or null when it has no date or has
  // not happened yet. Nothing is overdue until there is a date to reckon from.
  const roundDate = round.date ? new Date(round.date) : null
  const daysSinceRound =
    roundDate && !Number.isNaN(roundDate.getTime()) && roundDate.getTime() < Date.now()
      ? Math.floor((Date.now() - roundDate.getTime()) / DAY_MS)
      : null

  // A recorded outcome closes the round: once staff know it was an offer, a
  // rejection, or settled silence, chasing a thank-you or a follow-up is moot
  // and nagging about it would be noise.
  // Proceeding to another round is the one outcome that is not an ending, so
    // it must NOT silence the thank-you and follow-up nudges — that is the
    // moment a thank-you matters most. This predicate was written when every
    // outcome was an ending, and truthiness alone got it wrong the moment one
    // was not.
    const isConcluded = !!round.outcome && round.outcome !== 'proceed_to_next_stage'
  const thankYouOverdue =
    !round.thankYouSentAt && !isConcluded && daysSinceRound !== null && daysSinceRound >= THANK_YOU_DUE_AFTER_DAYS
  const followUpOverdue =
    !round.followUpSentAt && !isConcluded && daysSinceRound !== null && daysSinceRound >= FOLLOW_UP_DUE_AFTER_DAYS

  // Saved on blur rather than on change: a datetime-local fires onChange for
  // every partial value the picker passes through, and each one would be a write.
  const handleDateBlur = async () => {
    const saved = toDateTimeLocalInput(round.date)
    if (dateInput === saved) return
    const parsed = dateInput ? new Date(dateInput) : null
    if (parsed && Number.isNaN(parsed.getTime())) {
      showError('Enter a valid date and time.')
      setDateInput(saved)
      return
    }
    setSavingDate(true)
    try {
      await onUpdateRound(round.roundNumber, { date: parsed ? parsed.toISOString() : null })
    } catch (error) {
      console.error('Failed to update the round date:', error)
      showError('Could not save the interview date. Please try again.')
      setDateInput(saved)
    } finally {
      setSavingDate(false)
    }
  }

  const handleFollowThroughChange = async (updates: Record<string, unknown>) => {
    setSavingFollowThrough(true)
    try {
      await onUpdateRound(round.roundNumber, updates)
    } catch (error) {
      console.error('Failed to update round follow-through:', error)
      showError('Could not save that change. Please try again.')
    } finally {
      setSavingFollowThrough(false)
    }
  }

  const handleSaveDebrief = async () => {
    setSaving(true)
    try {
      await onUpdateRound(round.roundNumber, { debriefNotes: debriefNotes.trim() })
      setDebriefOpen(false)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await onDeleteRound(round.roundNumber)
      setConfirmDeleteOpen(false)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          // On a phone the delete control drops under the content instead of
          // squeezing it.
          gridTemplateColumns: { xs: '28px minmax(0, 1fr)', sm: '28px minmax(0, 1fr) auto' },
          alignItems: 'flex-start',
          columnGap: 1.5,
          p: 1.75,
          border: '1px solid',
          // Only a round that is waiting on a debrief takes the warning border.
          borderColor: needsDebrief ? 'warning.main' : 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        {/* Round number badge */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: 'text.primary',
            color: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: '0.75rem',
            fontVariantNumeric: 'tabular-nums',
            flexShrink: 0,
          }}
        >
          {round.roundNumber}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {formatLabels[round.type] || round.type}
            </Typography>
            <Chip
              label={round.status}
              size="small"
              variant="outlined"
              color={statusColors[round.status] || 'default'}
              sx={{ fontSize: '0.7rem', height: 22, textTransform: 'capitalize' }}
            />
            {needsDebrief && (
              <Chip
                label="Debrief needed"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontSize: '0.68rem', height: 20 }}
              />
            )}
          </Box>

          {/* The date is what the follow-through reminders below reckon from,
              so it is editable wherever this card is. That includes the client
              portal, which passes readOnly as false deliberately — a client
              logging their own interview date is intended. */}
          {readOnly ? (
            round.date && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {new Date(round.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </Typography>
            )
          ) : (
            <TextField
              label="Interview date"
              type="datetime-local"
              size="small"
              value={dateInput}
              onChange={(e) => setDateInput(e.target.value)}
              onBlur={handleDateBlur}
              InputLabelProps={{ shrink: true }}
              disabled={savingDate}
              sx={{ mt: 1.25, maxWidth: 240 }}
            />
          )}

          {(round.interviewerName || round.interviewerTitle) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {[round.interviewerName, round.interviewerTitle].filter(Boolean).join(' — ')}
            </Typography>
          )}

          {round.notes && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {round.notes}
            </Typography>
          )}

          {round.debriefNotes && (
            <Box sx={{ mt: 1.25, px: 1.25, py: 1, borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
              <Typography variant="caption" fontWeight={600} color="success.dark">Debrief Notes</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{round.debriefNotes}</Typography>
            </Box>
          )}

          {/* Follow-through — the thank-you note, the one-week follow-up and
              the result, which is what MissFit's own tracker follows a round
              by. Always rendered, including when nothing has been logged: an
              unsent thank-you that says so is the whole point, where a blank
              gap reads as "nothing to do here". */}
          <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip
              label={
                round.thankYouSentAt
                  ? `Thank-you sent ${formatSentOn(round.thankYouSentAt)}`.trim()
                  : thankYouOverdue
                    ? 'Thank-you overdue'
                    : 'Thank-you not sent'
              }
              size="small"
              variant="outlined"
              color={round.thankYouSentAt ? 'success' : thankYouOverdue ? 'warning' : 'default'}
              sx={{ fontSize: '0.68rem', height: 20 }}
            />
            <Chip
              label={
                round.followUpSentAt
                  ? `Follow-up sent ${formatSentOn(round.followUpSentAt)}`.trim()
                  : followUpOverdue
                    ? 'Follow-up overdue'
                    : 'Follow-up not sent'
              }
              size="small"
              variant="outlined"
              color={round.followUpSentAt ? 'success' : followUpOverdue ? 'warning' : 'default'}
              sx={{ fontSize: '0.68rem', height: 20 }}
            />
            <Chip
              label={round.outcome ? `Outcome: ${OUTCOME_LABELS[round.outcome]}` : 'Outcome not recorded'}
              size="small"
              variant="outlined"
              color={round.outcome ? OUTCOME_COLORS[round.outcome] : 'default'}
              sx={{ fontSize: '0.68rem', height: 20 }}
            />
          </Box>

          {!readOnly && (
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                // The Outcome select carries its label above the control, so
                // the buttons line up with the control, not the label.
                alignItems: 'flex-end',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Button
                size="small"
                variant="outlined"
                color={thankYouOverdue ? 'warning' : 'primary'}
                disabled={savingFollowThrough}
                onClick={() =>
                  handleFollowThroughChange({
                    thankYouSentAt: round.thankYouSentAt ? null : new Date().toISOString(),
                  })
                }
              >
                {round.thankYouSentAt ? 'Undo thank-you' : 'Mark thank-you sent'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color={followUpOverdue ? 'warning' : 'primary'}
                disabled={savingFollowThrough}
                onClick={() =>
                  handleFollowThroughChange({
                    followUpSentAt: round.followUpSentAt ? null : new Date().toISOString(),
                  })
                }
              >
                {round.followUpSentAt ? 'Undo follow-up' : 'Mark follow-up sent'}
              </Button>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id={`round-${round.roundNumber}-outcome-label`}>Outcome</InputLabel>
                <Select
                  labelId={`round-${round.roundNumber}-outcome-label`}
                  id={`round-${round.roundNumber}-outcome`}
                  label="Outcome"
                  // The empty value is a real option ("Not recorded"), so it has to
                  // render as one instead of leaving the field blank.
                  displayEmpty
                  value={round.outcome ?? ''}
                  disabled={savingFollowThrough}
                  onChange={(e) =>
                    // The "Not recorded" option carries an empty value, which
                    // has to reach the API as an explicit null — undefined
                    // would be dropped and the outcome would never clear.
                    handleFollowThroughChange({ outcome: (e.target.value as string) || null })
                  }
                >
                  <MenuItem value="">Not recorded</MenuItem>
                  {(Object.keys(OUTCOME_LABELS) as InterviewRoundOutcome[]).map((value) => (
                    <MenuItem key={value} value={value}>
                      {OUTCOME_LABELS[value]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {savingFollowThrough && <CircularProgress size={18} aria-label="Saving" />}
              {needsDebrief && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => setDebriefOpen(true)}
                >
                  Log Debrief
                </Button>
              )}
            </Box>
          )}
        </Box>

        {/* Delete */}
        {!readOnly && (
          <Tooltip title={`Delete round ${round.roundNumber}`}>
            <IconButton
              size="small"
              color="error"
              aria-label={`Delete round ${round.roundNumber}`}
              onClick={() => setConfirmDeleteOpen(true)}
              sx={{
                mt: -0.5,
                width: { xs: 44, sm: 34 },
                height: { xs: 44, sm: 34 },
                gridColumn: { xs: 2, sm: 'auto' },
                justifySelf: 'start',
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Debrief dialog */}
      <DialogShell
        open={debriefOpen}
        onClose={() => setDebriefOpen(false)}
        maxWidth="sm"
        title={`Round ${round.roundNumber} Debrief`}
        footer={
          <>
            <Button onClick={() => setDebriefOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSaveDebrief} variant="contained" disabled={saving || !debriefNotes.trim()}>
              {saving ? 'Saving...' : 'Save Debrief'}
            </Button>
          </>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          How did the interview go? What went well? What could improve?
        </Typography>
        <TextField
          autoFocus
          multiline
          rows={5}
          fullWidth
          value={debriefNotes}
          onChange={(e) => setDebriefNotes(e.target.value)}
          placeholder="What questions were asked? How did you feel about your answers? Any follow-up needed?"
        />
      </DialogShell>

      {/* Delete confirmation */}
      <DialogShell
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        maxWidth="xs"
        compact
        title={`Delete Round ${round.roundNumber}?`}
        footer={
          <>
            <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </>
        }
      >
        <Typography>
          {round.debriefNotes
            ? 'This will permanently delete this round and its debrief notes. It cannot be undone.'
            : 'This will permanently delete this round. Remaining rounds will be renumbered.'}
        </Typography>
      </DialogShell>
    </>
  )
}
